import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash, UserCircle } from "@phosphor-icons/react";

export default function TeamPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Team member added");
      setForm({ name: "", email: "", password: "", role: "editor" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="font-serif text-3xl font-light">Team</h2>
      <p className="text-sm text-muted-foreground mt-1">People who can manage the studio content.</p>

      <div className="mt-8 grid md:grid-cols-2 gap-10">
        <form onSubmit={add} className="space-y-4" data-testid="team-form">
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Add member</p>
          <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} data-testid="team-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} data-testid="team-email" />
          <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={cls} data-testid="team-password" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={cls}>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
          <button disabled={busy} data-testid="team-add" className="inline-flex items-center gap-2 bg-foreground text-background text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-accent transition-colors disabled:opacity-50">
            <Plus size={16} /> Add Member
          </button>
        </form>

        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Members</p>
          <div className="divide-y divide-border border border-border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-4 py-4" data-testid={`team-member-${u.email}`}>
                <UserCircle size={30} className="text-foreground/40" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email} · {u.role}</p>
                </div>
                {u.email !== user?.email && (
                  <button onClick={() => remove(u.id)} className="text-destructive"><Trash size={16} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const cls = "w-full bg-transparent border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground transition-colors";
